import { Packr } from "msgpackr";
import type { Gear, GearSlot, Inventory, ItemId, Wear } from "./items";
import type { EditTarget } from "./world/physics";
import type { AreaCoord, SphereConfig } from "./world/areas";
import type { AsteroidDef, Look, PlayerInput, PlayerState, ShipState } from "./world/types";

export const PROTOCOL_VERSION = 38;

/** A ship's tiles on the wire: its grid size (ships grow, see SHIP_GROWTH), `encodeTiles` of it, and its shaped tiles (see encodeShapes), if any. */
export interface ShipTiles {
  w: number;
  h: number;
  tiles: string;
  shapes?: [number, number][];
}

/** Messages the client sends. */
export type ClientMessage =
  /**
   * Play as your character on this socket. `ticket` says who you are: from
   * POST /api/session (on the Node server, the saved token works too). On
   * a socket already open to view an area, this is how you take your
   * character there (see "handoff").
   */
  | { t: "join"; version: number; name: string; ticket?: string; look?: Look }
  /**
   * Watch an area you're near but not in (within NET.viewRadius of it), so
   * what's across the border is drawn too. Nothing you send here moves you.
   */
  | { t: "view"; version: number; ticket?: string; x: number; y: number }
  /** Where a viewing socket's player is now (a few times a second), for what it's sent. */
  | { t: "look"; x: number; y: number }
  /** Input steps, packed (see encodeInputs): the client sends its 60 Hz steps three to a message (at once for a click or a press), and none while it's standing still. */
  | { t: "inputs"; data: Uint8Array }
  /** Place `tile` (a cell: the tile id, plus its shape, see packCell; spending its item), or dig/remove when `tile` is 0 (EMPTY), collecting what it drops. */
  | { t: "edit"; target: EditTarget; tile: number }
  /** Take back the last block you placed (if it was only just now, see UNDO): it comes out free. */
  | { t: "undo" }
  /**
   * Start over (for when you are lost or stuck): wake up in your bed if you
   * have one, else on a rock in the start system, as new players do.
   */
  | { t: "respawn" }
  /** Put on one `item` you carry (something worn: see GearSlot); what was in its slot comes off into your inventory. */
  | { t: "equip"; item: ItemId }
  /** Take off what you wear in `slot`, into your inventory. */
  | { t: "unequip"; slot: GearSlot }
  /**
   * Jump to a world point picked on the map. At the helm the ship (and its
   * crew) goes; on foot just you. Lands on the nearest open spot.
   */
  | { t: "teleport"; x: number; y: number }
  /** Make one batch of a recipe (see RECIPES); some need a workbench within reach. */
  | { t: "craft"; recipe: string }
  /** Feed the furnace within reach: just `item` if given (fuel or water), else one fuel and one water. */
  | { t: "stoke"; item?: ItemId }
  /** Throw water (one, from your inventory) on a burning tile within reach (ship or asteroid): it and the fire beside it go out. */
  | { t: "douse"; target: EditTarget }
  /** Open the hatch (or door) at `target` if it's shut, shut it if it's open (it must be within reach). */
  | { t: "hatch"; target: EditTarget }
  /**
   * Look in the chest (or crate) at `target` (within reach), and with `move`,
   * move items: `count` > 0 puts that many of `item` in, < 0 takes them out.
   * The server answers with a "chest" message of what's in it.
   */
  | { t: "chest"; target: EditTarget; move?: { item: ItemId; count: number } }
  /** Read the sign at `target`, or (with `text`, within reach) write on it. The server answers with a "sign" message. */
  | { t: "sign"; target: EditTarget; text?: string }
  /** Sleep in the bed at `target` (within reach): you'll wake up there when knocked out. */
  | { t: "bed"; target: EditTarget }
  /** Mend the cracked block at `target` (within reach) with a patch kit. */
  | { t: "repair"; target: EditTarget }
  /** Switch the winch at `target` (within reach) on or off. */
  | { t: "winch"; target: EditTarget }
  /** Eat something (puffcap spores) to heal. */
  | { t: "eat"; item: ItemId }
  | { t: "chat"; text: string }
  | { t: "ping"; sent: number };

/** Messages the server sends. */
export type ServerMessage =
  | {
      t: "welcome";
      playerId: string;
      /** Session token to resend on reconnect (the Node server's guests); empty when tickets come from /api/session. */
      token: string;
      worldSeed: number;
      /** The sphere and the region this socket is to (its key, see regionKey; null: the whole sphere is one area). */
      sphere: SphereConfig;
      region: string | null;
      tick: number;
      players: PlayerState[];
      ships: ShipState[];
      /** Each ship's grid, keyed by ship id. */
      shipTiles: Record<string, ShipTiles>;
      /** The area's asteroids as they are now: edits, rocks formed since, dead rocks, cut vines. */
      rocks: AreaRocks;
      /** What you are carrying. */
      inventory: Inventory;
      /** How worn your tools are. */
      wear: Wear;
      /** What you wear. */
      gear: Gear;
    }
  /**
   * The world near you, 20 times a second: a SNAPSHOT packet (see
   * snapshot.ts) of the players, ships, cannonballs and creatures within
   * NET.viewRadius, the suns flaring or out, fires, rifts and the rest.
   * Only what came into view is in it whole, and only what changed since
   * the last one is in it at all (the socket is ordered and reliable, so
   * nothing is missed). A ship that comes into range is sent (with its
   * tiles) as a "ship" message first.
   */
  | { t: "snapshot"; data: Uint8Array }
  /** A viewing socket's welcome: what's in this area near you (see "view"). */
  | {
      t: "viewing";
      tick: number;
      sphere: SphereConfig;
      region: string | null;
      players: PlayerState[];
      ships: ShipState[];
      shipTiles: Record<string, ShipTiles>;
      /** The area's asteroids as they are now (see AreaRocks). */
      rocks: AreaRocks;
    }
  /**
   * These ships and players crossed into area `to`: keep drawing them until
   * that area's socket has them. If you're one of them, stop sending input
   * here; "owned" comes from `to`.
   */
  | { t: "handoff"; to: string; ships: string[]; players: string[] }
  /** This socket now has your character: send input here (and resend any not yet acknowledged). */
  | { t: "owned"; playerId: string }
  /** A ship appeared, was reset or grew. */
  | ({ t: "ship"; state: ShipState } & ShipTiles)
  /** One tile changed: `tile` is a cell (the tile id plus its shape, see packCell). */
  | { t: "tile"; kind: "ship" | "asteroid"; id: string; index: number; tile: number }
  /** A new rock formed (see NEW_ROCKS), out of sight. */
  | { t: "rock"; def: AsteroidDef }
  /** A living rock's heart was taken: it's a husk now (see AsteroidDef.dead). */
  | { t: "dead"; id: string }
  /** A vine was cut through, or grew back. */
  | { t: "vine"; id: string; cut: boolean }
  /** A plant on asteroid `id` came down (`felled`) or grew back: all its tiles at once, [index, tile]. */
  | { t: "plant"; id: string; changes: [number, number][]; felled: boolean }
  | { t: "left"; playerId: string }
  /** Your inventory (or what you wear) changed. */
  | { t: "inventory"; items: Inventory; wear: Wear; gear: Gear }
  /** What's in the chest whose anchor (bottom-left tile) is `target`, after you looked or moved things. */
  | { t: "chest"; target: EditTarget; items: Inventory }
  /** What the sign at `target` says. */
  | { t: "sign"; target: EditTarget; text: string }
  /** Something that happened to you, to show on screen (e.g. being knocked out). */
  | { t: "notice"; text: string }
  /**
   * A one-off effect everyone nearby should hear or see: a cannonball hit, a
   * crash (`power`: how hard, in m/s lost), someone boarding the ship you're on.
   */
  | { t: "fx"; kind: "hit" | "impact" | "board" | "roar" | "rift" | "snuffed" | "pulled"; x: number; y: number; power?: number }
  | { t: "chat"; from: string; text: string }
  | { t: "pong"; sent: number; serverTime: number }
  | { t: "error"; message: string };

/**
 * Messages travel as MessagePack: binary, several times smaller than JSON and
 * faster to encode. Records mode writes each object shape once per message,
 * so a snapshot full of players does not repeat every key.
 */
const packr = new Packr({ useRecords: true, moreTypes: false });

export function encode(msg: ClientMessage | ServerMessage): Uint8Array {
  // Packr writes into a shared scratch buffer; copy so a queued socket send can never see it change.
  return packr.pack(msg).slice();
}

function decode<T extends { t: string }>(raw: Uint8Array): T | null {
  try {
    const m = packr.unpack(raw) as T;
    return typeof m?.t === "string" ? m : null;
  } catch {
    return null;
  }
}

export const decodeClient = (raw: Uint8Array) => decode<ClientMessage>(raw);
export const decodeServer = (raw: Uint8Array) => decode<ServerMessage>(raw);

/**
 * An area's asteroids as they are now, on top of what generation made: sent
 * to clients as they join or watch, and to neighbours that have them loaded.
 */
export interface AreaRocks {
  /** Every player edit to the area's asteroids: [asteroidId, tileIndex, cell] (the tile id plus its shape, see packCell). */
  edits: [string, number, number][];
  /** Rocks formed since the world was made (see NEW_ROCKS). */
  formed: AsteroidDef[];
  /** Living rocks whose heart was taken. */
  dead: string[];
  /** Vines cut through. */
  vinesCut: string[];
}
